import { ConfidenceBadge } from '../common/ConfidenceBadge';
import type { ConfidenceLevel, AlternativeIdentification } from '../../types/api';

interface IdentificationResultProps {
  userPhotoUrl: string;
  referencePhotoUrl?: string;
  commonName: string;
  scientificName: string;
  confidence: ConfidenceLevel;
  alternatives?: AlternativeIdentification[];
  onConfirm: () => void;
  onSelectAlternative?: (alt: AlternativeIdentification) => void;
  onManualEntry: () => void;
  onNotMyPlant?: () => void;
  loading?: boolean;
}

export function IdentificationResult({
  userPhotoUrl,
  referencePhotoUrl,
  commonName,
  scientificName,
  confidence,
  alternatives,
  onConfirm,
  onSelectAlternative,
  onManualEntry,
  onNotMyPlant,
  loading,
}: IdentificationResultProps) {
  return (
    <div className="space-y-6">
      {/* Photo comparison */}
      <div className="flex gap-3">
        <div className="flex-1">
          <p className="mb-2 text-xs font-sans uppercase tracking-wider text-gray-400">Your photo</p>
          <div className="aspect-square overflow-hidden rounded-lg bg-white border border-gray-200">
            <img
              src={userPhotoUrl}
              alt="Your plant"
              className="h-full w-full object-cover"
            />
          </div>
        </div>

        {referencePhotoUrl && (
          <div className="flex-1">
            <p className="mb-2 text-xs font-sans uppercase tracking-wider text-gray-400">Reference</p>
            <div className="aspect-square overflow-hidden rounded-lg bg-white border border-gray-200">
              <img
                src={referencePhotoUrl}
                alt={`Reference ${commonName}`}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}
      </div>

      {/* Identification card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-display font-bold text-editorial-black">{commonName}</h2>
          <ConfidenceBadge confidence={confidence} />
        </div>

        <p className="italic font-serif text-gray-500">{scientificName}</p>

        {confidence === 'low' && (
          <p className="mt-3 text-sm font-serif text-gray-400">
            We&rsquo;re not certain about this match. Check the alternatives below or enter the name manually.
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="space-y-3">
        <button
          onClick={onConfirm}
          disabled={loading}
          className="btn-editorial group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              <span>Confirming...</span>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
                check
              </span>
              <span>Yes, that&rsquo;s it!</span>
            </>
          )}
        </button>

        {onNotMyPlant && (
          <button
            onClick={onNotMyPlant}
            disabled={loading}
            className="flex w-full h-12 items-center justify-center gap-2 rounded-lg border border-editorial-gold/50 bg-editorial-gold/5 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-editorial-gold disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">
              close
            </span>
            <span>Not my plant</span>
          </button>
        )}

        <button
          onClick={onManualEntry}
          disabled={loading}
          className="flex w-full h-12 items-center justify-center gap-2 rounded-lg border border-gray-300 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Enter name manually
        </button>
      </div>

      {/* Alternative suggestions */}
      {alternatives && alternatives.length > 0 && onSelectAlternative && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 section-header">
            Could it be one of these?
          </h3>
          <div className="space-y-2">
            {alternatives.map((alt, index) => (
              <button
                key={index}
                onClick={() => onSelectAlternative(alt)}
                disabled={loading}
                className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-background px-4 py-3 text-left transition-colors hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <div>
                  <p className="font-display font-bold text-editorial-black">{alt.commonName}</p>
                  <p className="text-sm font-serif italic text-gray-500">{alt.scientificName}</p>
                </div>
                <span className="material-symbols-outlined text-gray-400" style={{ fontSize: 20 }} aria-hidden="true">
                  arrow_forward
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
